import { useStaticQuery, graphql } from "gatsby";
import { getContentData } from "./helperFunctions";

/**
 * getQueriedFluidImage
 *
 * Gets fluid object from image queried from GraphQL.
 * If no fluid object was found, returns a placeholder image instead.
 *
 * To ensure that it works, the input image object
 * has to be queried with the following fields:
 *
 * image {  <-- should match field name from Strapi.
 *   id
 *   childImageSharp {
 *     id
 *     fluid {
 *       src
 *       srcSet
 *       srcSetWebp
 *       srcWebp
 *       aspectRatio
 *       sizes
 *     }
 *   }
 * }
 *
 * @param {object} queriedImageObj
 */


export const getImageUrl = imageURL => {

  return `${process.env.STRAPI_API_URL}${imageURL}`
};

/**
 * getSSRImageData
 *
 * Similar to getSSRData, get image object of the desired locale
 * if the locale exists and has a property named 'image'.
 * Else, returns the 'image' property of the global object as a fallback.
 *
 * @param {object} globalData
 * @param {object} data
 * @param {string} fieldName
 * @param {string} locale
 * @param {string} imageFieldName
 */
export const getSSRImageData = (data, fieldName, locale, imageFieldName = "image") => {
  let localizedFieldName = `${fieldName}`;
  let globalFieldName = `${fieldName}`;


  if (data[localizedFieldName] && data[localizedFieldName][imageFieldName]) {
    return data[localizedFieldName][imageFieldName];
  }
  return data[globalFieldName][imageFieldName];
};

export const getBackgroundImageData = (globalData, data, fieldName, locale, imageFieldName = "background_image") => {
  let localizedFieldName = `${fieldName}`;
  let globalFieldName = `${fieldName}`;


  if (data[localizedFieldName] && data[localizedFieldName][imageFieldName]) {
    return data[localizedFieldName][imageFieldName];
  }
  return globalData[localizedFieldName][imageFieldName];
};

export const getImageData = (globalData, data, fieldName, locale, imageFieldName = "image") => {
  let localizedFieldName = `${fieldName}`;
  let globalFieldName = `${fieldName}`;


  if (data[localizedFieldName] && data[localizedFieldName][imageFieldName]) {
    return data[localizedFieldName][imageFieldName];
  }
  return globalData[localizedFieldName][imageFieldName];
};

export const getImageAltData = (globalData, data) => {

  if (data && data) {
    return data;
  }
  return null;
};



export const getQueriedFluidImage = queriedImageObj => {
  if (queriedImageObj && queriedImageObj.localFile) {
    return queriedImageObj.localFile;
  }
  return null;
};







export const PlaceholderImage = () => {
  return useStaticQuery(graphql`
    query {
      placeholder: file(relativePath: { eq: "placeholder.png" }) {
        id
        childImageSharp {
          fluid {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
    }
  `);
};



/**
 * getLocaleSensitiveFluidImage
 *
 * Currying of getSSRImageData() and getQueriedFluidImage().
 *
 * Gets the Fluid Image object of the desired locale.
 * If no image object is found, falls back to a placeholder image.
 *
 * @param {object} data
 * @param {string} fieldName
 * @param {string} locale
 * @param {string} imageFieldName
 */
export const getLocaleSensitiveFluidImage = (
  data,
  fieldName,
  locale,
  imageFieldName = "image"
) => {
  return getQueriedFluidImage(getSSRImageData(data, fieldName, locale, imageFieldName));
};

/**
 * getContentImage
 *
 * Gets the Image object of the targeted locale's ContentData.
 * If the desired locale has no Image object, falls back to the global locale.
 *
 * @param {object} PageContent
 * @param {string} dataField
 * @param {string} locale
 * @param {string} filter
 * @param {string} imageFieldName
 */
export const getContentImage = (
  PageContent,
  dataField,
  locale,
  filter,
  imageFieldName = "image"
) => {
  const globalData = getContentData(PageContent, dataField, "global", filter);
  if (locale === "global") {
    return globalData[imageFieldName];
  }
  const localizedData = getContentData(PageContent, dataField, locale, filter);
  if (localizedData[imageFieldName]) {
    return localizedData[imageFieldName];
  }
  return globalData[imageFieldName];
};
